import React from "react";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

export default function HibidUsersTab({
  CUSTOMER_TYPE_OPTIONS,
  accountManagerFilter,
  accountManagerFilterPillOptions,
  accountManagerSavingByEmail,
  allFilteredUsers,
  allFilteredUsersSelected,
  applyUsersSearch,
  buyerRetentionAuctionCount,
  buyerRetentionMatchMode,
  buyerRetentionReportBusy,
  clearUsersSearch,
  customerTypeFilter,
  customerTypeFilterOptions,
  customerTypeLabel,
  customerTypeSavingByEmail,
  formatNumber2,
  getAccountManagerSelectOptionsForUser,
  getAvgHammerFromWin,
  getBidIntensity90,
  getBidsCount,
  getBpi90,
  getCustomerType,
  getDisplayBidderName,
  getDisplayUsername,
  getEffectiveTier,
  getFirstLastNameParts,
  getHibidBids90Count,
  getOverallHammerPrice,
  getLcvEstimate,
  getLotsWonFromWin,
  getPickupLocationSelectOptions,
  getRegs12mCount,
  getRegsCount,
  getResolvedAccountManagerIdForUser,
  getRollingWin,
  getUserEmailLower,
  isEmail,
  lastEmailFilter,
  lastEmailFilterOptions,
  moneyPlain,
  newSubFilterOptions,
  newTierSubFilter,
  onUsersTableScroll,
  openAccountManagerModal,
  openConsignorModal,
  openEmailForLoadedUsers,
  openEmailForUser,
  openEdit,
  openUserRegistrationsModal,
  orgReady,
  primary,
  deleteRow,
  regsFilterOptions,
  regsThresholdFilter,
  reloadUsersFromServer,
  downloadBuyerRetentionTable,
  downloadFastRevenueReport,
  fastRevenueReportBusy,
  pickupLocationMetadataNames,
  safeNum,
  search,
  selectedFilteredUsers,
  setAccountManagerFilter,
  setBuyerRetentionAuctionCount,
  setBuyerRetentionMatchMode,
  setCustomerTypeFilter,
  setLastEmailFilter,
  setNewTierSubFilter,
  setRegsThresholdFilter,
  setTierFilter,
  setUserSortDir,
  setUserSortKey,
  showUsersLoadingModal,
  someFilteredUsersSelected,
  tierChipSx,
  tierFilter,
  tierFilterOptions,
  toAbsInt,
  toggleSelectAllUsers,
  toggleSelectUser,
  updateAccountManagerForUser,
  updateCustomerTypeForUser,
  updateUserPickupDefaultsForUser,
  userHasMore,
  userLoadingMore,
  userSelected,
  userSortDir,
  userSortKey,
  userTableBottomSpacerHeight,
  userTableContainerRef,
  userTableTopSpacerHeight,
  usersInitialLoading,
  usersSearchInputRef,
  usersTableLoading,
  usersTotalHeaderText,
  visibleUsers,
}) {
  const usersFilterFieldSx = {
    background: "#fff",
    borderRadius: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      fontWeight: 700,
    },
    "& .MuiInputLabel-root": {
      fontWeight: 700,
    },
  };
  const [buyerRetentionModalOpen, setBuyerRetentionModalOpen] = React.useState(false);
  const [pickupLocationDraftByEmail, setPickupLocationDraftByEmail] = React.useState({});
  const [pickupDefaultsSavingByEmail, setPickupDefaultsSavingByEmail] = React.useState({});

  const updatePickupDefaultsDraft = React.useCallback(async (row, nextPartial = {}) => {
    const emailLower = getUserEmailLower(row);
    if (!isEmail(emailLower)) return;

    const currentLocation =
      nextPartial.defaultPickupLocationName !== undefined
        ? String(nextPartial.defaultPickupLocationName || "").trim()
        : String(
            pickupLocationDraftByEmail[emailLower] !== undefined
              ? pickupLocationDraftByEmail[emailLower]
              : row?.defaultPickupLocationName || ""
          ).trim();

    if (nextPartial.defaultPickupLocationName !== undefined) {
      setPickupLocationDraftByEmail((prev) => ({ ...prev, [emailLower]: currentLocation }));
    }
    setPickupDefaultsSavingByEmail((prev) => ({ ...prev, [emailLower]: true }));

    try {
      await updateUserPickupDefaultsForUser(row, {
        defaultPickupLocationName: currentLocation,
      });
    } catch (error) {
      if (nextPartial.defaultPickupLocationName !== undefined) {
        setPickupLocationDraftByEmail((prev) => {
          const next = { ...prev };
          delete next[emailLower];
          return next;
        });
      }
    } finally {
      setPickupDefaultsSavingByEmail((prev) => {
        const next = { ...prev };
        delete next[emailLower];
        return next;
      });
    }
  }, [getUserEmailLower, isEmail, pickupLocationDraftByEmail, updateUserPickupDefaultsForUser]);

  return (
    <>
      <Dialog
        open={showUsersLoadingModal}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 320,
            maxWidth: 380,
            p: 0.5,
          },
        }}
      >
        <DialogContent sx={{ py: 3.5, px: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={30} />
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>
              Loading users, please wait
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={buyerRetentionModalOpen}
        onClose={() => {
          if (!buyerRetentionReportBusy) setBuyerRetentionModalOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "#0f172a",
            pb: 1,
          }}
        >
          Buyer Retention Table
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography sx={{ color: "#475569", mb: 2 }}>
            Choose the retention filter here, then download the report from this modal.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
            <TextField
              size="small"
              type="number"
              label="Auction Count"
              value={buyerRetentionAuctionCount}
              onChange={(e) => setBuyerRetentionAuctionCount(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              sx={usersFilterFieldSx}
            />

            <TextField
              select
              size="small"
              label="Retention Match"
              value={buyerRetentionMatchMode}
              onChange={(e) => setBuyerRetentionMatchMode(e.target.value)}
              sx={usersFilterFieldSx}
            >
              <MenuItem value="exact">Use exact number</MenuItem>
              <MenuItem value="lte">Use &lt;= number</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label="24h cache"
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                backgroundColor: "#eef2ff",
                color: "#333999",
                border: "1px solid rgba(51,57,153,0.12)",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: "space-between" }}>
          <Button
            onClick={() => setBuyerRetentionModalOpen(false)}
            disabled={buyerRetentionReportBusy}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              color: "#475569",
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => void downloadBuyerRetentionTable()}
            disabled={!orgReady || !allFilteredUsers?.length || buyerRetentionReportBusy || usersInitialLoading || usersTableLoading}
            startIcon={buyerRetentionReportBusy ? <CircularProgress size={16} /> : <ListAltIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2.5,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#333999,#1d4ed8)",
              "&:hover": { background: "linear-gradient(90deg,#2f2f8b,#1e40af)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
            }}
          >
            {buyerRetentionReportBusy ? "Building Retention..." : "Download Buyer Retention"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: { xs: "100%", md: 760 }, flex: 1 }}>
          <TextField
            defaultValue=""
            inputRef={usersSearchInputRef}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyUsersSearch();
              }
            }}
            size="small"
            placeholder="Type search and press Enter or click search"
            sx={{ width: { xs: "100%", md: 520 }, background: "#fff", borderRadius: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Apply search (Enter)">
                    <span>
                      <IconButton size="small" onClick={applyUsersSearch}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {search ? (
                    <IconButton size="small" onClick={clearUsersSearch}>
                      ✕
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" sx={{ color: "#64748b", ml: 0.5 }}>
            Search runs only on Enter or the search icon.
          </Typography>
          <Paper
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid rgba(51,57,153,0.12)",
              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 1.25,
                width: "100%",
              }}
            >
              <TextField
                select
                size="small"
                label="Tier"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                sx={usersFilterFieldSx}
              >
                {tierFilterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="My Regs"
                value={regsThresholdFilter}
                onChange={(e) => setRegsThresholdFilter(e.target.value)}
                sx={usersFilterFieldSx}
              >
                {regsFilterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Last Registration"
                value={lastEmailFilter}
                onChange={(e) => setLastEmailFilter(e.target.value)}
                sx={usersFilterFieldSx}
              >
                {lastEmailFilterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Customer Type"
                value={customerTypeFilter}
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
                sx={usersFilterFieldSx}
              >
                {customerTypeFilterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Account Manager"
                value={accountManagerFilter}
                onChange={(e) => setAccountManagerFilter(e.target.value)}
                sx={usersFilterFieldSx}
              >
                {accountManagerFilterPillOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              {tierFilter === "new" ? (
                <TextField
                  select
                  size="small"
                  label="New Filter"
                  value={newTierSubFilter}
                  onChange={(e) => setNewTierSubFilter(e.target.value)}
                  sx={usersFilterFieldSx}
                >
                  {newSubFilterOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Box>
          </Paper>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            width: { xs: "100%", xl: "auto" },
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, max-content))" },
            alignItems: "stretch",
            pt: { xs: 0, md: 0.25 },
          }}
        >
          <Paper
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              border: "1px solid rgba(51,57,153,0.12)",
              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
              minWidth: { xs: "100%", lg: 520 },
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.4, color: "#475569", textTransform: "uppercase", mb: 1 }}>
              Reports
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                onClick={() => void reloadUsersFromServer()}
                disabled={!orgReady || usersInitialLoading || usersTableLoading}
                startIcon={usersInitialLoading || usersTableLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#0d9488,#0f766e)",
                  "&:hover": { background: "linear-gradient(90deg,#0f766e,#115e59)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
                }}
              >
                {usersInitialLoading || usersTableLoading ? "Reloading..." : "Reload Users"}
              </Button>

              <Button
                onClick={() => void downloadFastRevenueReport()}
                disabled={!orgReady || !allFilteredUsers?.length || fastRevenueReportBusy || usersInitialLoading || usersTableLoading}
                startIcon={fastRevenueReportBusy ? <CircularProgress size={16} /> : <ListAltIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#7c3aed,#6d28d9)",
                  "&:hover": { background: "linear-gradient(90deg,#6d28d9,#5b21b6)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
                }}
              >
                {fastRevenueReportBusy ? "Building Report..." : "Fast Revenue Report"}
              </Button>

              <Button
                onClick={() => setBuyerRetentionModalOpen(true)}
                disabled={!orgReady || !allFilteredUsers?.length || buyerRetentionReportBusy || usersInitialLoading || usersTableLoading}
                startIcon={buyerRetentionReportBusy ? <CircularProgress size={16} /> : <ListAltIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#333999,#1d4ed8)",
                  "&:hover": { background: "linear-gradient(90deg,#2f2f8b,#1e40af)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.85)" },
                }}
              >
                {buyerRetentionReportBusy ? "Building Retention..." : "Buyer Retention Table"}
              </Button>
            </Box>
          </Paper>

          <Paper
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              border: "1px solid rgba(51,57,153,0.12)",
              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
              minWidth: { xs: "100%", lg: 380 },
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.4, color: "#475569", textTransform: "uppercase", mb: 1 }}>
              Team Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                onClick={() => {
                  void openAccountManagerModal("create");
                }}
                disabled={!orgReady}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.20)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Account Manager
              </Button>

              <Button
                onClick={() => {
                  void openConsignorModal();
                }}
                disabled={!orgReady}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.20)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                Consignors
              </Button>

              <Button
                onClick={openEmailForLoadedUsers}
                disabled={!orgReady || !allFilteredUsers?.length}
                startIcon={<MailOutlineIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.25,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#6366f1,#4f46e5)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#4338ca)" },
                }}
              >
                Email users
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            mb: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>
            Users
            <Box component="span" sx={{ ml: 1, color: "#6b7280", fontWeight: 800 }}>
              ({usersTotalHeaderText})
            </Box>
          </Typography>

          <Chip
            size="small"
            label={`Selected: ${formatNumber2(selectedFilteredUsers.length)}`}
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              backgroundColor: "rgba(14,165,233,0.10)",
              color: "#0369a1",
              border: "1px solid rgba(14,165,233,0.22)",
            }}
          />
        </Box>

        <TableContainer
          component={Paper}
          ref={userTableContainerRef}
          onScroll={onUsersTableScroll}
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(51,57,153,0.10)",
            maxHeight: "68vh",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: "fixed",
              width: 2290,
              minWidth: 2290,
              "& th, & td": {
                whiteSpace: "nowrap",
              },
            }}
          >
            <colgroup>
              <col style={{ width: 56 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 190 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 230 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 260 }} />
              <col style={{ width: 230 }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <Checkbox
                    size="small"
                    checked={allFilteredUsersSelected}
                    indeterminate={someFilteredUsersSelected && !allFilteredUsersSelected}
                    onChange={toggleSelectAllUsers}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "bidderName"}
                    direction={userSortKey === "bidderName" ? userSortDir : "asc"}
                    onClick={() => {
                      if (userSortKey === "bidderName") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("bidderName");
                        setUserSortDir("asc");
                      }
                    }}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "bidderName"}
                    direction={userSortKey === "bidderName" ? userSortDir : "asc"}
                    onClick={() => {
                      if (userSortKey === "bidderName") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("bidderName");
                        setUserSortDir("asc");
                      }
                    }}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "customerType"}
                    direction={userSortKey === "customerType" ? userSortDir : "asc"}
                    onClick={() => {
                      if (userSortKey === "customerType") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("customerType");
                        setUserSortDir("asc");
                      }
                    }}
                  >
                    Customer Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "tier"}
                    direction={userSortKey === "tier" ? userSortDir : "asc"}
                    onClick={() => {
                      if (userSortKey === "tier") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("tier");
                        setUserSortDir("asc");
                      }
                    }}
                  >
                    Tier
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  Account Manager
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "overallHammer"}
                    direction={userSortKey === "overallHammer" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "overallHammer") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("overallHammer");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    Overall Hammer
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "lcv"}
                    direction={userSortKey === "lcv" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "lcv") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("lcv");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    LCV
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "regs"}
                    direction={userSortKey === "regs" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "regs") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("regs");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    Regs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "bids"}
                    direction={userSortKey === "bids" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "bids") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("bids");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    My Bids
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "hibidBids90"}
                    direction={userSortKey === "hibidBids90" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "hibidBids90") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("hibidBids90");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    HiBid Bids (90d)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "regs12m"}
                    direction={userSortKey === "regs12m" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "regs12m") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("regs12m");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    Regs 12m
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  <TableSortLabel
                    active={userSortKey === "bids12m"}
                    direction={userSortKey === "bids12m" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "bids12m") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("bids12m");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    Bids 12m
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary, minWidth: 260 }}>
                  <TableSortLabel
                    active={userSortKey === "lots90"}
                    direction={userSortKey === "lots90" ? userSortDir : "desc"}
                    onClick={() => {
                      if (userSortKey === "lots90") setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else {
                        setUserSortKey("lots90");
                        setUserSortDir("desc");
                      }
                    }}
                  >
                    Lots 90d
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, color: primary }}>
                  Pickup Location
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 900, color: primary, minWidth: 174 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!usersTableLoading && allFilteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16}>
                    <Box sx={{ p: 5, textAlign: "center", color: "#6b7280" }}>No users found.</Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {userTableTopSpacerHeight > 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} sx={{ p: 0, height: userTableTopSpacerHeight, borderBottom: "none" }} />
                    </TableRow>
                  ) : null}
                  {visibleUsers.map((r) => {
                    const email = r.email || r.id;
                    const emailLower = getUserEmailLower(r);
                    const { firstName, lastName } = getFirstLastNameParts(getDisplayBidderName(r));
                    const displayUsername = getDisplayUsername(r);
                    const overallHammer = getOverallHammerPrice(r);
                    const lcv = getLcvEstimate(r);
                    const regsCount = getRegsCount(r);
                    const bidsCount = getBidsCount(r);
                    const hibidBids90Count = getHibidBids90Count(r);
                    const regs12mCount = getRegs12mCount(r);
                    const bids12mCount = toAbsInt(
                      r?.totalUniqueBidCountLast12MonthsValue ??
                      r?.totalUniqueBidCountLast12Months ??
                      r?.bids12m ??
                      0
                    );
                    const tier = getEffectiveTier(r);
                    const type = getCustomerType(r);
                    const managerId = getResolvedAccountManagerIdForUser(r);
                    const managerOptions = getAccountManagerSelectOptionsForUser(r);
                    const typeOptions =
                      type && !CUSTOMER_TYPE_OPTIONS.includes(type)
                        ? [...CUSTOMER_TYPE_OPTIONS, type]
                        : CUSTOMER_TYPE_OPTIONS;
                    const isCustomerTypeSaving = Boolean(customerTypeSavingByEmail[emailLower]);
                    const isAccountManagerSaving = Boolean(accountManagerSavingByEmail[emailLower]);
                    const win90 = getRollingWin(r, "win90");
                    const lots90 = getLotsWonFromWin(win90);
                    const avg90 = getAvgHammerFromWin(win90);
                    const bidIntensity90 = getBidIntensity90(r);
                    const bpi90 = getBpi90(r);
                    const checked = userSelected?.has?.(emailLower);
                    const pickupLocationValue =
                      pickupLocationDraftByEmail[emailLower] !== undefined
                        ? String(pickupLocationDraftByEmail[emailLower] || "")
                        : String(r?.defaultPickupLocationName || "").trim();
                    const pickupDefaultsSaving = Boolean(pickupDefaultsSavingByEmail[emailLower]);
                    const pickupLocationOptions = getPickupLocationSelectOptions([
                      ...pickupLocationMetadataNames,
                      pickupLocationValue,
                    ]);

                    return (
                      <TableRow key={email} hover>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={!!checked}
                            disabled={!isEmail(emailLower)}
                            onChange={() => toggleSelectUser(r)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, color: "#111827" }}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Button
                              onClick={() => openEdit(r)}
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                                minWidth: 0,
                                px: 0,
                                py: 0,
                                fontWeight: 900,
                                color: "#111827",
                                "&:hover": { background: "transparent", textDecoration: "underline" },
                              }}
                            >
                              {firstName || "—"}
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, color: "#111827" }}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Button
                              onClick={() => openEdit(r)}
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                                minWidth: 0,
                                px: 0,
                                py: 0,
                                fontWeight: 900,
                                color: "#111827",
                                "&:hover": { background: "transparent", textDecoration: "underline" },
                              }}
                            >
                              {lastName || "—"}
                            </Button>
                            {displayUsername ? <Typography variant="caption" sx={{ color: "#6b7280" }}>{displayUsername}</Typography> : null}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={String(type || "")}
                              displayEmpty
                              disabled={isCustomerTypeSaving}
                              onChange={(evt) => {
                                void updateCustomerTypeForUser(r, evt.target.value);
                              }}
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 700,
                                backgroundColor: "#fff",
                                "& .MuiSelect-select": { py: 0.75 },
                              }}
                            >
                              <MenuItem value="">
                                <em>Unassigned</em>
                              </MenuItem>
                              {typeOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {customerTypeLabel(opt)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {isCustomerTypeSaving ? (
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              Saving...
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {tier ? (
                              <Chip size="small" label={`${tier}`} sx={tierChipSx(tier)} />
                            ) : (
                              <Chip size="small" label="—" sx={{ borderRadius: 999, opacity: 0.7, fontWeight: 900 }} variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={String(managerId || "")}
                              displayEmpty
                              disabled={isAccountManagerSaving}
                              onChange={(evt) => {
                                void updateAccountManagerForUser(r, evt.target.value);
                              }}
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 700,
                                backgroundColor: "#fff",
                                "& .MuiSelect-select": { py: 0.75 },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Account Manager</em>
                              </MenuItem>
                              {managerOptions.map((opt) => (
                                <MenuItem key={`${emailLower}_manager_${opt.id}`} value={opt.id}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {isAccountManagerSaving ? (
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              Saving...
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 900, color: "#111827" }}>
                          {overallHammer != null ? `$${moneyPlain(overallHammer)}` : "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, color: "#111827" }}>
                          {lcv != null ? `$${moneyPlain(lcv)}` : "—"}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => openUserRegistrationsModal(r)}
                            startIcon={<ListAltIcon />}
                            sx={{ minWidth: 0, px: 1.25, borderRadius: 999, fontWeight: 900, lineHeight: 1.1, mt: 2 }}
                            variant="outlined"
                          >
                            {formatNumber2(regsCount)}
                          </Button>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={formatNumber2(bidsCount)}
                            sx={{ borderRadius: 999, fontWeight: 900 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatNumber2(hibidBids90Count)}
                            sx={{ borderRadius: 999, fontWeight: 900 }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatNumber2(regs12mCount)}
                            sx={{ borderRadius: 999, fontWeight: 900 }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatNumber2(bids12mCount)}
                            sx={{ borderRadius: 999, fontWeight: 900 }}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell sx={{ minWidth: 260 }}>
                          {win90 ? (
                            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                              {lots90 != null ? <Chip size="small" label={`Lots ${formatNumber2(lots90)}`} sx={{ borderRadius: 999 }} variant="outlined" /> : null}
                              {avg90 != null ? <Chip size="small" label={`Avg $${moneyPlain(avg90)}`} sx={{ borderRadius: 999 }} variant="outlined" /> : null}
                              {bidIntensity90 > 0 ? (
                                <Chip size="small" label={`Intensity ${safeNum(bidIntensity90).toFixed(2)}x`} sx={{ borderRadius: 999 }} variant="outlined" />
                              ) : null}
                              {bpi90 > 0 ? <Chip size="small" label={`BPI ${safeNum(bpi90).toFixed(2)}`} sx={{ borderRadius: 999 }} variant="outlined" /> : null}
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell sx={{ minWidth: 220 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={pickupLocationValue}
                              displayEmpty
                              disabled={pickupDefaultsSaving}
                              onChange={(evt) => {
                                void updatePickupDefaultsDraft(r, {
                                  defaultPickupLocationName: evt.target.value,
                                });
                              }}
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 700,
                                backgroundColor: "#fff",
                                "& .MuiSelect-select": { py: 0.75 },
                              }}
                            >
                              <MenuItem value="">
                                <em>No default location</em>
                              </MenuItem>
                              {pickupLocationOptions.map((option) => (
                                <MenuItem key={`${emailLower}_pickup_location_${option}`} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {pickupDefaultsSaving ? (
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              Saving...
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell align="center" sx={{ minWidth: 220 }}>
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              onClick={() => openEmailForUser(r)}
                              startIcon={<MailOutlineIcon />}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                px: 2,
                                fontWeight: 900,
                                color: "#fff",
                                background: "linear-gradient(90deg,#0ea5e9,#0284c7)",
                                "&:hover": { background: "linear-gradient(90deg,#0284c7,#0369a1)" },
                              }}
                            >
                              Email
                            </Button>
                            <Button
                              onClick={() => deleteRow(r)}
                              startIcon={<DeleteOutlineIcon />}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                px: 2,
                                fontWeight: 900,
                                color: "#991b1b",
                                border: "1px solid rgba(220,38,38,0.22)",
                                background: "#fff",
                                "&:hover": { background: "rgba(254,226,226,0.8)" },
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {userTableBottomSpacerHeight > 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} sx={{ p: 0, height: userTableBottomSpacerHeight, borderBottom: "none" }} />
                    </TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            borderTop: "1px solid #e5e7eb",
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 56,
          }}
        >
          {userLoadingMore ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
                Loading more users...
              </Typography>
            </Box>
          ) : userHasMore ? (
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Scroll to load more users
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              End of results
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
}
